import { type SkillSummary } from "@/shared/domain/skills";

import { type SkillSelection } from "../buildRequestTemplate";

/** Mirror of the backend ``MAX_AVAILABLE_SKILLS`` cap on a session's explicit
 *  skill set. Every attached skill becomes a line in the agent's LoadSkillTool
 *  registry, so an unbounded set bloats the per-turn prompt. */
export const MAX_AVAILABLE_SKILLS = 100;

export interface SkillSelectionError {
  /** Index into ``FormState.skills``. ``-1`` marks a list-level error. */
  index: number;
  message: string;
}

/**
 * Pure validator for the agent's selected skills.
 *
 * ``orgPublicSkills`` is the set of org-public skills currently visible to the
 * author. Call this only once that fetch has settled — an unsettled (still
 * empty) list would flag every selection as unavailable.
 *
 * Flags three conditions, each of which the backend also rejects:
 * - a ``skill_id`` that no longer resolves to a visible org-public skill
 *   (deleted, or downgraded to private since it was attached);
 * - a duplicate ``skill_id`` (the per-session skill set must be unique);
 * - more than {@link MAX_AVAILABLE_SKILLS} entries.
 *
 * An empty selection is valid — the backend treats it as "no skills".
 */
export function validateSkills(
  skills: SkillSelection[],
  orgPublicSkills: SkillSummary[],
): SkillSelectionError[] {
  const errors: SkillSelectionError[] = [];
  const available = new Set(orgPublicSkills.map((s) => s.skill.skill_id));
  const seenAtRow = new Map<string, number>();
  skills.forEach((selection, index) => {
    if (!available.has(selection.skill_id)) {
      errors.push({
        index,
        message: "This skill is no longer available — remove it.",
      });
    }
    const previousRow = seenAtRow.get(selection.skill_id);
    if (previousRow !== undefined) {
      errors.push({
        index,
        message: `Duplicate of the skill at row ${previousRow + 1}.`,
      });
    } else {
      seenAtRow.set(selection.skill_id, index);
    }
  });
  if (skills.length > MAX_AVAILABLE_SKILLS) {
    errors.push({
      index: -1,
      message: `At most ${MAX_AVAILABLE_SKILLS} skills can be attached to an agent.`,
    });
  }
  return errors;
}

/** Convenience: true when no validator on the selection would block a save. */
export function allSkillsValid(
  skills: SkillSelection[],
  orgPublicSkills: SkillSummary[],
): boolean {
  return validateSkills(skills, orgPublicSkills).length === 0;
}
